// Shared business logic for the Home v2 screen (mobile-web build).
//
// Both the desktop and the mobile layouts of `DashboardScreen.web.tsx` consume
// this hook so they call the SAME controllers, dispatch the SAME actions and
// stay in sync across window resizes. Rendering files are presentation-only.
import { nanoid } from 'nanoid'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Animated } from 'react-native'

import { isSmartAccount } from '@ambire-common/libs/account/account'
import formatDecimals from '@ambire-common/utils/formatDecimals/formatDecimals'
import shortenAddress from '@ambire-common/utils/shortenAddress'
import useNavigation from '@common/hooks/useNavigation'
import { TabType } from '@common/modules/dashboard/components/TabsAndSearch/Tabs/Tab/Tab'
import { storage } from '@common/services/storage'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'
import useDashboardAssets from '@common/modules/dashboard/hooks/useDashboardAssets'
import useDashboardRecentActivity from '@common/modules/dashboard/hooks/useDashboardRecentActivity'
import { useDashboardPrivatePendingRow } from '@common/modules/dashboard/hooks/useDashboardPrivatePendingRow'
import { useRailgunOverviewBalance } from '@common/modules/dashboard/hooks/useRailgunOverviewBalance'
import { useRailgunShieldPending } from '@common/modules/dashboard/hooks/useRailgunShieldPending'
import {
  resolveDashboardNetworkFilterForModeChange,
  resolveExtensionPrivateInvariantFilter
} from '@common/modules/dashboard/utils/dashboardModeNetworkFilter'
import { WEB_ROUTES } from '@common/modules/router/constants/common'
import { isExtension } from '@web/constants/browserapi'
import { EXTENSION_PRIVATE_CHAIN_IDS } from '@web/constants/extensionChains'
import { Mode, ModeEnum } from '@web/contexts/appModeContext'
import { useRailgunMerkletreeScanContext } from '@web/contexts/railgunContext'
import useAppMode from '@web/hooks/useAppMode'
import useBackgroundService from '@web/hooks/useBackgroundService'
import useExtensionAwareNetworks from '@web/hooks/useExtensionAwareNetworks'
import useKeystoreControllerState from '@web/hooks/useKeystoreControllerState'
import useRailgunContext from '@web/hooks/useRailgunContext'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import useWalletStateController from '@web/hooks/useWalletStateController'
import { FEATURES } from '@web/constants/features'
import { computePrivateModeReason, isPrivateModeBlocked } from '@web/utils/privateModeAvailability'
import { getBalanceUsdByChain } from '@web/utils/railgunTokens'

// The token-row shape lives with the shared derivation hook now; re-exported
// here so the Home v2 components keep importing it from this module.
export type { AssetRowData } from '@common/modules/dashboard/hooks/useDashboardAssets'

// How long the token list may keep showing the skeleton on the strength of a
// known balance alone, before conceding there are simply no rows to show.
const DATA_GAP_GRACE_MS = 8000

const useHomeDashboard = () => {
  const {
    mode,
    setMode,
    railgunEngineInitializing,
    railgunEngineInitializingError,
    ensureRailgunEngine
  } = useAppMode()
  const { navigate } = useNavigation()
  const { dispatch } = useBackgroundService()
  const { account, portfolio, dashboardNetworkFilter } = useSelectedAccountControllerState()
  const { networks } = useExtensionAwareNetworks()
  const {
    currentRailgunWallet,
    allRailgunWallets,
    isLoading: railgunIsLoading,
    isBalanceFetchingError
  } = useRailgunContext()
  const { keys } = useKeystoreControllerState()
  const railgunOverview = useRailgunOverviewBalance(mode)
  const { scanningProgress } = useRailgunMerkletreeScanContext()

  const { networks: allNetworks } = useNetworksControllerState()

  // Hide-balance lives in WalletStateController (persisted `isPrivacyModeEnabled`),
  // not local state — otherwise it resets every time Home unmounts (Trello #509).
  const { isPrivacyModeEnabled } = useWalletStateController()
  const hidden = !!isPrivacyModeEnabled
  const [selectedTokenId, setSelectedTokenId] = useState<string | null>(null)
  // "Hide token" confirmation popup + its "Don't ask me again" opt-out (persisted).
  const [hideTokenModalVisible, setHideTokenModalVisible] = useState(false)
  const [doNotAskHideToken, setDoNotAskHideToken] = useState(false)
  // Asset-type tab (tokens / collectibles / defi) — same model as the previous
  // dashboard. Drives the toolbar "asset types" dropdown.
  const [openTab, setOpenTab] = useState<TabType>('tokens')
  const [initTab, setInitTab] = useState<{ [key: string]: boolean }>({ tokens: true })
  const [sessionId] = useState(() => `home-${nanoid()}`)
  // Stable animated value to satisfy the existing list components' API. Home
  // doesn't run the collapse animation, so it stays at 0.
  const animatedOverviewHeight = useRef(new Animated.Value(0)).current

  // Search — same control wired into the real Search input; filters tokens.
  const {
    control: searchControl,
    watch,
    setValue
  } = useForm<{ search: string }>({
    mode: 'all',
    defaultValues: { search: '' }
  })
  const searchValue = watch('search')

  const isPrivate = mode === ModeEnum.PRIVATE

  // Lazy-init a tab the first time it's opened (mirrors DashboardPages).
  useEffect(() => {
    if (!initTab[openTab]) setInitTab((prev) => ({ ...prev, [openTab]: true }))
  }, [openTab, initTab])

  // Register the home session so Activity sessions terminate on unmount. The
  // sessionId is kept in local state only (not mirrored into the URL) — on
  // mobile-web cleanup runs from the effect-return dispatch below, so the URL
  // doesn't need to carry it for the extension's port-disconnect handler.
  useEffect(() => {
    return () => {
      dispatch({ type: 'MAIN_CONTROLLER_ACTIVITY_RESET_ACC_OPS_FILTERS', params: { sessionId } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sessionId])

  // Reset to the tokens tab when the wallet mode changes.
  useEffect(() => {
    setOpenTab('tokens')
  }, [mode])

  // Recent-activity panel (public only) — wires the REAL ActivityController under
  // the home session, same as the full Activity screen. Shares this hook's
  // sessionId with the asset-type tabs, so cleanup stays with the ACTIVITY_RESET
  // dispatch in the session-registration effect above.
  // No age window: the panel used to drop everything older than 24h, so a wallet
  // that hadn't transacted today showed "No recent activity" while the Activity
  // screen was full of ops. It now shows the latest ops whatever their age.
  const { recentActivity } = useDashboardRecentActivity({ isPrivate, sessionId })

  const dashboardNetworkFilterName = useMemo(() => {
    if (!dashboardNetworkFilter) return null
    if (dashboardNetworkFilter === 'rewards') return 'Rewards'
    if (dashboardNetworkFilter === 'gasTank') return 'Gas Tank'
    return (
      allNetworks.find((n) => n.chainId.toString() === dashboardNetworkFilter.toString())?.name ||
      null
    )
  }, [dashboardNetworkFilter, allNetworks])

  const hasRailgunWallet = useMemo(
    () => allRailgunWallets.some((w) => w.accountAddr === account?.addr),
    [allRailgunWallets, account?.addr]
  )

  const privateModeReason = useMemo(() => {
    const base = computePrivateModeReason({ account, keys, hasRailgunWallet })
    if (base !== 'creatable') return base
    if (railgunEngineInitializingError) return 'failed'
    if (railgunEngineInitializing) return 'initializing'
    return base
  }, [account, keys, hasRailgunWallet, railgunEngineInitializing, railgunEngineInitializingError])

  // Private balance broken down per chain — used both to pick the default private
  // network and to scope the "cached balance but no rows yet" skeleton guard to
  // the network the user is actually filtering on.
  const railgunUsdByChain = useMemo(() => {
    const balanceTokenMap = currentRailgunWallet?.balanceTokenMap
    const prices = currentRailgunWallet?.balanceTokenPriceMap
    return balanceTokenMap && prices ? getBalanceUsdByChain(balanceTokenMap, prices) : undefined
  }, [currentRailgunWallet?.balanceTokenMap, currentRailgunWallet?.balanceTokenPriceMap])

  const firstPrivateSupportedChainId = useMemo(() => {
    const set = new Set(EXTENSION_PRIVATE_CHAIN_IDS.map((id) => id.toString()))
    const exists: Record<string, true> = {}
    networks.forEach((n) => {
      exists[n.chainId.toString()] = true
    })
    const usdByChain = railgunUsdByChain
    const sorted = networks
      .map((n) => n.chainId.toString())
      .filter((chainId) => set.has(chainId) && exists[chainId])
      .sort((a, b) => (usdByChain?.get(Number(b)) ?? 0) - (usdByChain?.get(Number(a)) ?? 0))
    return sorted.length ? sorted[0] : EXTENSION_PRIVATE_CHAIN_IDS[0]?.toString()
  }, [railgunUsdByChain, networks])

  const handleModeChange = useCallback(
    (nextMode: Mode) => {
      setSelectedTokenId(null)
      const nextFilter = resolveDashboardNetworkFilterForModeChange({
        previousMode: mode,
        nextMode,
        currentFilter: dashboardNetworkFilter,
        isExtension,
        firstPrivateSupportedChainId
      })
      if (nextFilter !== undefined) {
        dispatch({
          type: 'SELECTED_ACCOUNT_SET_DASHBOARD_NETWORK_FILTER',
          params: { dashboardNetworkFilter: nextFilter }
        })
      }
      if (nextMode === ModeEnum.PRIVATE) {
        setTimeout(() => setMode(nextMode), 50)
      } else {
        setMode(nextMode)
      }
    },
    [mode, dashboardNetworkFilter, dispatch, firstPrivateSupportedChainId, setMode]
  )

  // Bounce back to public when private mode isn't usable: either no railgun
  // wallet exists, or the account can never go private (view-only / hardware /
  // raw private-key). The blocked check is what handles a view-only account that
  // still carries a stale railgun wallet row — hasRailgunWallet alone wouldn't.
  useEffect(() => {
    if (mode !== ModeEnum.PRIVATE) return
    if (railgunIsLoading) return
    // Init-on-demand: when the user opts into private mode on a seed account that
    // has no Railgun wallet yet, the engine boots and the wallet is created lazily.
    // 'creatable' (pre-boot) and 'initializing' (booting) are that in-flight window
    // — don't bounce back to public, or the toggle would snap off mid-setup.
    if (privateModeReason === 'creatable' || privateModeReason === 'initializing') return
    if (hasRailgunWallet && !isPrivateModeBlocked(privateModeReason)) return
    handleModeChange(ModeEnum.PUBLIC)
  }, [hasRailgunWallet, privateModeReason, mode, handleModeChange, railgunIsLoading])

  // Keep a valid private-supported chain selected when entering private mode.
  useEffect(() => {
    if (!isExtension || mode !== ModeEnum.PRIVATE || !firstPrivateSupportedChainId) return
    const next = resolveExtensionPrivateInvariantFilter(
      dashboardNetworkFilter,
      firstPrivateSupportedChainId
    )
    if (next !== undefined) {
      dispatch({
        type: 'SELECTED_ACCOUNT_SET_DASHBOARD_NETWORK_FILTER',
        params: { dashboardNetworkFilter: next }
      })
    }
  }, [mode, dashboardNetworkFilter, dispatch, firstPrivateSupportedChainId])

  // ---- Balances ---------------------------------------------------------
  // GAS_TANK_ENABLED=false: GasTankPill is hidden, so drop the internal
  // `gasTank` slice from the headline number too (totalBalance sums it in).
  const publicTotal = FEATURES.gasTankUiEnabled
    ? portfolio?.totalBalance || 0
    : Math.max(0, (portfolio?.totalBalance || 0) - (portfolio?.balancePerNetwork?.gasTank || 0))
  const privateTotal = railgunOverview.totalRailgunBalanceForDisplay || 0
  const displayTotal = isPrivate ? privateTotal : publicTotal
  const balanceFormatted = formatDecimals(displayTotal, 'value')

  // Lazy init-on-demand: when the user clicks Private the engine boots BEFORE any
  // balance fetch starts, so `isRailgunBalanceFetching` is still false during that
  // window. Fold `railgunEngineInitializing` into the private loading flags so the
  // balance skeleton + spinning refresh icon light up the instant the user opts in
  // — one combined "loading private wallet" state instead of a dead gap before the
  // merkletree scan kicks off.
  const isBalanceLoading = isPrivate
    ? (railgunEngineInitializing || railgunOverview.isRailgunBalanceFetching) &&
      !railgunOverview.hasPriorBalance
    : !portfolio?.isReadyToVisualize

  // True whenever the private wallet is booting OR a balance fetch is in flight
  // (initial load OR background refresh). Drives the spinning refresh icon —
  // mirrors the extension's `isFetchingBalance`.
  const isSyncing = isPrivate
    ? railgunEngineInitializing || railgunOverview.isRailgunBalanceFetching
    : !portfolio?.isAllReady || !!portfolio?.isReloading

  // Private merkletree scan % — shown next to the refresh icon while a private
  // balance fetch is in flight (mirrors the extension DashboardOverview).
  const showScanProgress = isPrivate && railgunOverview.isRailgunBalanceFetching

  // ---- Pending private balance (shield processing + missing POI) --------
  // Mirrors the extension DashboardOverview pending row: a "Processing …" /
  // "N processing in Activity" shield badge and the "Incoming $X" pill for
  // received funds still awaiting POI. Hidden while the balance is fetching.
  const shouldShowBatchOrHint = isPrivate && railgunOverview.isRailgunBalanceFetching
  const shieldPending = useRailgunShieldPending(mode)
  const { totalMissingPoiUsd, showPrivatePendingRow } = useDashboardPrivatePendingRow({
    mode,
    shouldShowBatchOrHint,
    isRailgunBalanceFetching: railgunOverview.isRailgunBalanceFetching,
    totalMissingInternalPOIUSD: railgunOverview.totalMissingInternalPOIUSD,
    totalMissingExternalPOIUSD: railgunOverview.totalMissingExternalPOIUSD,
    shieldPending
  })

  // ---- Tokens -----------------------------------------------------------
  // Row filtering/sorting/formatting is shared with the extension's full-tab
  // dashboard side panel — see useDashboardAssets.
  const { assets, dustAssets, dustTotalUSD } = useDashboardAssets({ isPrivate, searchValue })

  // Only surface the "You don't have any tokens yet." empty state when the
  // balance for what's on screen is actually 0. An empty token list while a
  // balance is known is not a true "empty wallet" — it's a data gap (per-token
  // maps are in-memory only and get wiped on cold-load / worker re-init; the
  // aggregate total persists). In that window show the skeleton instead.
  // Mirrors the common Tokens.tsx cached-balance guard.
  //
  // The comparison must be SCOPED to the active network filter: `displayTotal`
  // is wallet-wide and stays > 0 on a chain the account holds nothing on, which
  // used to pin the skeleton on forever there (Trello #462). With a filter
  // active, compare against that chain's balance instead — 0 means the chain is
  // legitimately empty, so the empty state is correct.
  const scopedTotal = useMemo(() => {
    // 'rewards' / 'gasTank' don't scope the row list at all (see
    // useDashboardAssets) — the rows stay wallet-wide, so must the total.
    if (
      dashboardNetworkFilter === null ||
      dashboardNetworkFilter === undefined ||
      dashboardNetworkFilter === 'rewards' ||
      dashboardNetworkFilter === 'gasTank'
    )
      return displayTotal
    const filterKey = dashboardNetworkFilter.toString()
    if (isPrivate) return railgunUsdByChain?.get(Number(filterKey)) ?? 0
    return portfolio?.balancePerNetwork?.[filterKey] ?? 0
  }, [dashboardNetworkFilter, displayTotal, isPrivate, railgunUsdByChain, portfolio])

  // Dust-only accounts (every holding collapsed into the "other tokens" group)
  // still have rows to show, so count dust when deciding "no tokens".
  const totalRowCount = assets.length + dustAssets.length
  // Time-bound the data-gap guard. The gap it covers (per-token maps rehydrating
  // after a cold load) resolves in seconds; a balance that never resolves into
  // rows for another reason — every token on the chain hidden by the user, a
  // chain whose whole balance is a DeFi position — must NOT pin the skeleton on
  // forever. After the grace window we fall through to the empty state.
  const hasScopedBalanceButNoRows = scopedTotal > 0 && totalRowCount === 0
  const [dataGapGraceExpired, setDataGapGraceExpired] = useState(false)
  useEffect(() => {
    if (!hasScopedBalanceButNoRows) {
      setDataGapGraceExpired(false)
      return
    }
    const timeout = setTimeout(() => setDataGapGraceExpired(true), DATA_GAP_GRACE_MS)
    return () => clearTimeout(timeout)
    // Restart the window whenever the scope changes — a different chain/account
    // gets its own chance to rehydrate.
  }, [hasScopedBalanceButNoRows, dashboardNetworkFilter, isPrivate, account?.addr])
  const hasBalanceButNoTokens = hasScopedBalanceButNoRows && !dataGapGraceExpired
  const isTokensLoading = isPrivate
    ? (railgunOverview.isRailgunBalanceFetching || hasBalanceButNoTokens) && totalRowCount === 0
    : (!portfolio?.isAllReady || hasBalanceButNoTokens) && totalRowCount === 0

  // Private cold-sync gap: the merkletree scan finishes (isRailgunBalanceFetching
  // flips false) but POI verification keeps running in the scan worker, so
  // received funds are pending-POI and not yet in the spendable token list.
  // Without this the token area shows a false "You don't have any tokens yet."
  // while "Incoming $X" is on the balance card. Surface a "verifying" hint
  // instead. Gated on the same pending signals that drive the incoming pill.
  const isVerifyingPrivateBalance =
    isPrivate &&
    assets.length === 0 &&
    (showPrivatePendingRow ||
      !!shieldPending?.showBadge ||
      railgunOverview.isRailgunBalanceFetching)

  const selectedAsset = useMemo(
    // A row inside the expanded "other tokens" group is selectable too.
    () =>
      assets.find((a) => a.id === selectedTokenId) ||
      dustAssets.find((a) => a.id === selectedTokenId) ||
      null,
    [assets, dustAssets, selectedTokenId]
  )

  // ---- Hide token -------------------------------------------------------
  // Load the persisted "Don't ask me again" preference once (shared storage key
  // with the extension token-details flow).
  useEffect(() => {
    storage
      .get('doNotShowAgainModalHideToken', false)
      .then(setDoNotAskHideToken)
      .catch(() => console.error('Failed to load doNotShowAgainModalHideToken'))
  }, [])

  // Dispatch the actual hide and dismiss the detail panel. Mirrors the
  // extension's PortfolioController.toggleHideToken(token, accountAddr, true).
  const hideSelectedToken = useCallback(() => {
    if (!selectedAsset) return
    dispatch({
      type: 'method',
      params: {
        ctrlName: 'PortfolioController',
        method: 'toggleHideToken',
        args: [
          { address: selectedAsset.address, chainId: selectedAsset.chainId },
          account?.addr,
          true
        ]
      }
    } as any)
    setSelectedTokenId(null)
  }, [selectedAsset, account?.addr, dispatch])

  // Hide button on the detail panel: hide immediately when the user opted out of
  // the confirmation, otherwise open the confirm popup.
  const requestHideSelectedToken = useCallback(() => {
    if (doNotAskHideToken) hideSelectedToken()
    else setHideTokenModalVisible(true)
  }, [doNotAskHideToken, hideSelectedToken])

  const confirmHideToken = useCallback(
    (doNotAskAgain: boolean) => {
      if (doNotAskAgain) {
        setDoNotAskHideToken(true)
        storage
          .set('doNotShowAgainModalHideToken', true)
          .catch(() => console.error('Failed to persist doNotShowAgainModalHideToken'))
      }
      setHideTokenModalVisible(false)
      hideSelectedToken()
    },
    [hideSelectedToken]
  )

  const cancelHideToken = useCallback(() => setHideTokenModalVisible(false), [])

  // ---- Account identity -------------------------------------------------
  const accountName =
    account?.preferences?.label || (account?.addr ? shortenAddress(account.addr, 13) : '')
  const accountAddress = account?.addr || ''
  // In private mode the card shows the Railgun (0zk…) address, not the public 0x one.
  const displayAddress =
    isPrivate && currentRailgunWallet?.railgunAddress
      ? currentRailgunWallet.railgunAddress
      : accountAddress
  const accountPfp = account?.preferences?.pfp || account?.addr || ''
  const accountIsSmart = useMemo(() => (account ? isSmartAccount(account) : false), [account])

  // ---- Actions / navigation --------------------------------------------
  const refresh = useCallback(() => {
    if (isPrivate) {
      void railgunOverview.fetchRailgunBalance()
    } else {
      dispatch({
        type: 'MAIN_CONTROLLER_RELOAD_SELECTED_ACCOUNT',
        params: { chainId: dashboardNetworkFilter ?? undefined }
      })
    }
  }, [isPrivate, railgunOverview, dispatch, dashboardNetworkFilter])

  const goTo = useCallback((route: string) => navigate(route), [navigate])
  // Tapping the account avatar opens the account-select screen, mirroring the
  // extension's DashboardHeader AccountButton behavior.
  const goToAccountSelect = useCallback(() => navigate(WEB_ROUTES.accountSelect), [navigate])

  return {
    // mode
    mode,
    isPrivate,
    handleModeChange,
    privateModeReason,
    ensureRailgunEngine,
    railgunEngineInitializing,
    // identity
    accountName,
    accountAddress,
    displayAddress,
    accountPfp,
    accountIsSmart,
    goToAccountSelect,
    // balance
    balanceFormatted,
    isBalanceLoading,
    isSyncing,
    showScanProgress,
    scanningProgress,
    // "Initial load may take up to 15 minutes" hint — shown inside the balance
    // card after 15s of a first-time private balance fetch (mirrors the extension
    // DashboardOverview BatchOrFirstBalanceHint).
    showFirstBalanceLoadHint: railgunOverview.showFirstBalanceLoadHint,
    // pending private balance row (shield processing badge + missing-POI pill)
    shieldPending,
    totalMissingPoiUsd,
    showPrivatePendingRow,
    hidden,
    toggleHidden: () => dispatch({ type: 'TOGGLE_PRIVACY_MODE' }),
    refresh,
    // tokens
    assets,
    // dust/spam rows collapsed behind the "N other tokens" summary in AssetTable
    dustAssets,
    dustTotalUSD,
    isTokensLoading,
    isVerifyingPrivateBalance,
    // private-balance fetch failed (e.g. Railgun engine/IndexedDB dead after
    // incognito sleep) — surface a retry instead of a false "no tokens" state.
    privateBalanceError: isPrivate && isBalanceFetchingError,
    selectedAsset,
    selectedTokenId,
    selectToken: (id: string) => setSelectedTokenId((cur) => (cur === id ? null : id)),
    clearSelectedToken: () => setSelectedTokenId(null),
    // hide-token confirm popup
    hideTokenModalVisible,
    requestHideSelectedToken,
    confirmHideToken,
    cancelHideToken,
    // toolbar: network filter + asset-type tabs + search (previous-version flow)
    openTab,
    setOpenTab,
    sessionId,
    initTab,
    animatedOverviewHeight,
    allNetworks,
    dashboardNetworkFilter,
    dashboardNetworkFilterName,
    searchControl,
    clearSearch: () => setValue('search', ''),
    // recent activity panel (public)
    recentActivity,
    // totals for the privacy donut
    publicTotal,
    privateTotal,
    // navigation
    goTo,
    routes: WEB_ROUTES
  }
}

export default useHomeDashboard
